import {mergeCombatStats} from '../../../utils/gameState/buildCombatStats';

const initialState = {
  showRoundEndModal: false,
  showCityCaptureModal: false,
  showCombatResultsModal: false,
  showGameEndModal: false,
  showInitiativeModal: false,
  showRepairBuildingModal: false,
  repairBuildingHexKey: null,
  // Игрок, чей ход шел при открытии модалки ремонта (см. showRepairBuildingModal)
  repairBuildingPlayerId: null,
  showNoConstructionActionsModal: false,
  showSupplyFarmsConfirmModal: false,
  showSupplyRedistributeConfirmModal: false,
  // Подтверждение завершения этапа войны, пока есть отряды, способные действовать
  // (change unit-auto-selection; заменило окна Лепреконов, Суккубов и Гоблинов)
  showWarStageUnitsConfirmModal: false,
  showIsolationismModal: false,
  showConstructionPhaseInfoModal: false,
  initialRulesSectionId: null,
  // Раздел, который сейчас открыт в правилах (обновляет RulesModal)
  rulesActiveSectionId: null,
  hideModalsExceptGameEnd: false,
  // Идет облет захваченных городов на поле (см. scenes/CityCaptureAnimation):
  // модалка захвата и следующие за ней в очереди ждут его конца
  isCityCaptureAnimationPlaying: false,
  // Рост дохода руин на поле (см. scenes/RuinsIncomeAnimation): запрос ждет, пока
  // закроются итоги раунда и все, что перед ними; потом анимация идет
  isRuinsIncomeAnimationPending: false,
  isRuinsIncomeAnimationPlaying: false,
  // Итоги боя ждут ближнего боя, идущего следом: {stage, playerId} — этап и игрок,
  // на ком они придержаны (см. selectIsCombatResultsHeldForMelee)
  combatResultsMeleeHold: null,
  // Итоги прежних этапов, которые игрок еще не закрыл: {results, stats}
  carriedCombatResults: null,
};

const modalVisibilityReducer = (state = initialState, action) => {
  switch (action.type) {
    case 'SHOW_CITY_CAPTURE_MODAL': {
      return {
        ...state,
        showCityCaptureModal: true,
      };
    }

    case 'HIDE_CITY_CAPTURE_MODAL': {
      return {
        ...state,
        showCityCaptureModal: false,
      };
    }

    case 'START_CITY_CAPTURE_ANIMATION': {
      return {
        ...state,
        isCityCaptureAnimationPlaying: true,
      };
    }

    case 'FINISH_CITY_CAPTURE_ANIMATION': {
      return {
        ...state,
        isCityCaptureAnimationPlaying: false,
      };
    }

    case 'START_UNIT_DEATH_ANIMATION': {
      return {
        ...state,
        isUnitDeathAnimationPlaying: true,
      };
    }

    case 'FINISH_UNIT_DEATH_ANIMATION': {
      return {
        ...state,
        isUnitDeathAnimationPlaying: false,
      };
    }

    case 'SHOW_COMBAT_RESULTS_MODAL': {
      return {
        ...state,
        showCombatResultsModal: true,
      };
    }

    case 'SHOW_ROUND_END_MODAL': {
      return {
        ...state,
        showRoundEndModal: true,
      };
    }

    case 'HIDE_ROUND_END_MODAL': {
      return {
        ...state,
        showRoundEndModal: false,
      };
    }

    case 'HIDE_COMBAT_RESULTS_MODAL': {
      return {
        ...state,
        showCombatResultsModal: false,
        combatResultsMeleeHold: null,
        carriedCombatResults: null,
      };
    }

    case 'HOLD_COMBAT_RESULTS_FOR_MELEE': {
      return {
        ...state,
        combatResultsMeleeHold: action.payload,
      };
    }

    case 'RELEASE_COMBAT_RESULTS_FOR_MELEE': {
      return {
        ...state,
        combatResultsMeleeHold: null,
      };
    }

    case 'CARRY_COMBAT_RESULTS': {
      const {results, stats} = action.payload;
      const carried = state.carriedCombatResults;
      return {
        ...state,
        carriedCombatResults: {
          results: carried ? [...carried.results, ...results] : results,
          stats: carried ? mergeCombatStats(carried.stats, stats) : stats,
        },
      };
    }

    case 'REQUEST_RUINS_INCOME_ANIMATION': {
      return {
        ...state,
        isRuinsIncomeAnimationPending: true,
      };
    }

    case 'CANCEL_RUINS_INCOME_ANIMATION_REQUEST': {
      return {
        ...state,
        isRuinsIncomeAnimationPending: false,
      };
    }

    case 'START_RUINS_INCOME_ANIMATION': {
      return {
        ...state,
        isRuinsIncomeAnimationPending: false,
        isRuinsIncomeAnimationPlaying: true,
      };
    }

    case 'FINISH_RUINS_INCOME_ANIMATION': {
      return {
        ...state,
        isRuinsIncomeAnimationPlaying: false,
      };
    }

    case 'SHOW_GAME_END_MODAL': {
      return {
        ...state,
        showGameEndModal: true,
      };
    }

    case 'HIDE_GAME_END_MODAL': {
      return {
        ...state,
        showGameEndModal: false,
      };
    }

    case 'SHOW_INITIATIVE_MODAL': {
      return {
        ...state,
        showInitiativeModal: true,
      };
    }

    case 'HIDE_INITIATIVE_MODAL': {
      return {
        ...state,
        showInitiativeModal: false,
      };
    }

    case 'SHOW_REPAIR_BUILDING_MODAL': {
      return {
        ...state,
        showRepairBuildingModal: true,
        repairBuildingHexKey: action.payload.hexKey,
        repairBuildingPlayerId: action.payload.playerId ?? null,
      };
    }

    case 'HIDE_REPAIR_BUILDING_MODAL': {
      return {
        ...state,
        showRepairBuildingModal: false,
        repairBuildingHexKey: null,
        repairBuildingPlayerId: null,
      };
    }

    case 'SHOW_NO_CONSTRUCTION_ACTIONS_MODAL': {
      return {
        ...state,
        showNoConstructionActionsModal: true,
      };
    }

    case 'HIDE_NO_CONSTRUCTION_ACTIONS_MODAL': {
      return {
        ...state,
        showNoConstructionActionsModal: false,
      };
    }

    case 'SHOW_SUPPLY_FARMS_CONFIRM_MODAL': {
      return {
        ...state,
        showSupplyFarmsConfirmModal: true,
      };
    }

    case 'HIDE_SUPPLY_FARMS_CONFIRM_MODAL': {
      return {
        ...state,
        showSupplyFarmsConfirmModal: false,
      };
    }

    case 'SHOW_SUPPLY_REDISTRIBUTE_CONFIRM_MODAL': {
      return {
        ...state,
        showSupplyRedistributeConfirmModal: true,
      };
    }

    case 'HIDE_SUPPLY_REDISTRIBUTE_CONFIRM_MODAL': {
      return {
        ...state,
        showSupplyRedistributeConfirmModal: false,
      };
    }

    case 'SHOW_WAR_STAGE_UNITS_CONFIRM_MODAL': {
      return {
        ...state,
        showWarStageUnitsConfirmModal: true,
      };
    }

    case 'HIDE_WAR_STAGE_UNITS_CONFIRM_MODAL': {
      return {
        ...state,
        showWarStageUnitsConfirmModal: false,
      };
    }

    case 'SHOW_ISOLATIONISM_MODAL': {
      return {
        ...state,
        showIsolationismModal: true,
      };
    }

    case 'HIDE_ISOLATIONISM_MODAL': {
      return {
        ...state,
        showIsolationismModal: false,
      };
    }

    case 'SHOW_CONSTRUCTION_PHASE_INFO_MODAL': {
      return {
        ...state,
        showConstructionPhaseInfoModal: true,
        initialRulesSectionId: action.payload?.sectionId ?? null,
        rulesActiveSectionId: action.payload?.sectionId ?? state.rulesActiveSectionId,
      };
    }

    case 'SET_RULES_ACTIVE_SECTION': {
      return {
        ...state,
        rulesActiveSectionId: action.payload?.sectionId ?? null,
      };
    }

    case 'HIDE_CONSTRUCTION_PHASE_INFO_MODAL': {
      return {
        ...state,
        showConstructionPhaseInfoModal: false,
      };
    }

    case 'TOGGLE_HIDE_MODALS_EXCEPT_GAME_END': {
      return {
        ...state,
        hideModalsExceptGameEnd: !state.hideModalsExceptGameEnd,
      };
    }

    case 'SET_HIDE_MODALS_EXCEPT_GAME_END': {
      return {
        ...state,
        hideModalsExceptGameEnd: Boolean(action.payload),
      };
    }

    // Сброс "открыта ли модалка X" при уходе с фоновой демо-партии в главном
    // меню (см. MenuDemoGame) — та крутится на одном сторе с настоящей игрой,
    // и modalsMiddleware реагирует на ее конец раунда/партии как на настоящий,
    // выставляя эти флаги. Без сброса они переживали переход в реальную партию
    // и всплывали там на случайном раунде (пустая GameEndModal, лишняя
    // анимация дохода руин). hideModalsExceptGameEnd — не трогаем,
    // это отдельная пользовательская настройка, а не "модалка сейчас открыта".
    // showConstructionPhaseInfoModal (RulesModal) тоже не трогаем: она больше не
    // только игровая — открывается и с главного меню, где эта демо-партия и
    // крутится, поэтому каждый ее перезапуск не должен закрывать открытые правила
    case 'RESET_MODAL_VISIBILITY': {
      return {
        ...state,
        showRoundEndModal: false,
        showCityCaptureModal: false,
        showCombatResultsModal: false,
        showGameEndModal: false,
        showInitiativeModal: false,
        showRepairBuildingModal: false,
        repairBuildingHexKey: null,
        repairBuildingPlayerId: null,
        showNoConstructionActionsModal: false,
        showSupplyFarmsConfirmModal: false,
        showSupplyRedistributeConfirmModal: false,
        showWarStageUnitsConfirmModal: false,
        showIsolationismModal: false,
        isCityCaptureAnimationPlaying: false,
        isRuinsIncomeAnimationPending: false,
        isRuinsIncomeAnimationPlaying: false,
        combatResultsMeleeHold: null,
        carriedCombatResults: null,
      };
    }

    default:
      return state;
  }
};

export default modalVisibilityReducer;